import React from "react"
import PropTypes from "prop-types"
import { Button, Typography, Space, DatePicker, Flex } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { DATE_FILTER_OPTIONS, DATE_RANGE_FILTERS, DATE_SINGLE_FILTERS } from "../../constants"
import styles from "../../style.module.css"

const { RangePicker } = DatePicker
const { Text } = Typography

export default function AnalyticsHeader({
  activeDateFilter, setActiveDateFilter,
  dateRange, setDateRange,
  singleDate, setSingleDate,
  calendarOpen, setCalendarOpen,
  isEditing, setIsEditing,
  onAddWidget, onPeriodChange,
  hasWidgets
}) {
  const { t } = useTranslation()

  return (
    <Flex justify="space-between" align="center" wrap gap="var(--spacing-s)" className={styles.analyticsHeader}>
      <Text strong className={styles.sectionTitle}>{t("ai_dashboard.custom_analytics")}</Text>
      <Flex align="center" gap="var(--spacing-s)" wrap className={styles.analyticsHeaderRight}>
        {hasWidgets && (
          <Flex gap={2} wrap={false} className={styles.dateTabs}>
            {DATE_FILTER_OPTIONS.map((opt) => {
              const isRangeFilter = DATE_RANGE_FILTERS.includes(opt)
              const isSingleFilter = DATE_SINGLE_FILTERS.includes(opt)
              const isActive = activeDateFilter === opt
              const hasRange = isActive && dateRange[0] && dateRange[1]
              const hasSingle = isActive && singleDate

              const btn = (
                <button
                  key={opt}
                  className={`${styles.dateTab} ${isActive ? styles.dateTabActive : ""}`}
                  onClick={() => {
                    setActiveDateFilter(opt)
                    if (!isRangeFilter && !isSingleFilter) {
                      setDateRange([null, null]); setSingleDate(null); setCalendarOpen(false)
                      onPeriodChange(opt)
                    } else if (isRangeFilter) {
                      setSingleDate(null); setCalendarOpen(true)
                    } else if (isSingleFilter) {
                      setDateRange([null, null]); setCalendarOpen(true)
                    }
                  }}
                >
                  {opt}
                  {hasRange && <span className={styles.dateRangeLabel}>({dateRange[0].format("MMM D")} – {dateRange[1].format("MMM D")})</span>}
                  {hasSingle && <span className={styles.dateRangeLabel}>({singleDate.format("MMM D, YYYY")})</span>}
                </button>
              )

              if (!isRangeFilter && !isSingleFilter) return btn

              return (
                <span key={opt} className={styles.dateTabWrapper}>
                  {btn}
                  {isActive && isSingleFilter && (
                    <DatePicker
                      open={calendarOpen} value={singleDate || undefined}
                      onChange={(date) => { if (date) { setSingleDate(date); setCalendarOpen(false); onPeriodChange(activeDateFilter, null, date) } }}
                      onOpenChange={(open) => { if (!open) setCalendarOpen(false) }}
                      placement="bottomLeft" className={styles.hiddenPicker}
                    />
                  )}
                  {isActive && isRangeFilter && (
                    <RangePicker
                      open={calendarOpen} value={dateRange[0] && dateRange[1] ? dateRange : undefined}
                      onChange={(dates) => { if (dates?.[0] && dates?.[1]) { setDateRange(dates); setCalendarOpen(false); onPeriodChange(activeDateFilter, dates) } }}
                      onOpenChange={(open) => { if (!open) setCalendarOpen(false) }}
                      placement="bottomLeft" className={styles.hiddenPicker}
                    />
                  )}
                </span>
              )
            })}
          </Flex>
        )}
        <Space>
          <Button icon={<PlusOutlined />} onClick={onAddWidget}>{t("ai_dashboard.add_widget")}</Button>
          {hasWidgets && (
            isEditing
              ? <Button type="primary" onClick={() => setIsEditing(false)}>{t("ai_dashboard.save_changes")}</Button>
              : <Button onClick={() => setIsEditing(true)}>{t("ai_dashboard.customize_widgets")}</Button>
          )}
        </Space>
      </Flex>
    </Flex>
  )
}

AnalyticsHeader.propTypes = {
  activeDateFilter: PropTypes.string.isRequired,
  setActiveDateFilter: PropTypes.func.isRequired,
  dateRange: PropTypes.array.isRequired,
  setDateRange: PropTypes.func.isRequired,
  singleDate: PropTypes.object,
  setSingleDate: PropTypes.func.isRequired,
  calendarOpen: PropTypes.bool.isRequired,
  setCalendarOpen: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  onAddWidget: PropTypes.func.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  hasWidgets: PropTypes.bool,
}
